import React, { useState, useMemo } from 'react';
import { experiencesByPark } from '../../data/experiencesByPark';

const ExperienceSelector = ({ 
  formData, 
  onChange, 
  current = [] 
}) => {
  const [showAllExperiences, setShowAllExperiences] = useState(false);

  // Get selected parks from form data
  const selectedParks = formData?.parkVisitPlans?.parks || [];

  // Group experiences by park for better organization
  const experiencesByParkGrouped = useMemo(() => {
    const grouped = {};
    
    selectedParks.forEach(park => {
      if (experiencesByPark[park]) {
        grouped[park] = experiencesByPark[park];
      }
    });
    
    return grouped;
  }, [selectedParks]);

  const handleExperienceToggle = (experienceName) => {
    const isSelected = current.includes(experienceName);
    
    if (isSelected) {
      onChange("mustExperience", current.filter(name => name !== experienceName));
    } else {
      onChange("mustExperience", [...current, experienceName]);
    }
  };

  if (selectedParks.length === 0) {
    return (
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-700">
          Please select your parks first to see available premium experiences.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Show All Experiences Toggle */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowAllExperiences(!showAllExperiences)}
            className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${
              showAllExperiences
                ? 'text-white bg-blue-600 border-blue-600'
                : 'text-blue-600 bg-white border-blue-300 hover:bg-blue-50'
            }`}
          >
            {showAllExperiences ? 'Show All Experiences' : 'Show All Experiences'}
          </button>
        </div>
        <p className="text-sm text-gray-600">
          {current.length} selected
        </p>
      </div>

      {/* Experiences by Park */}
      <div className="space-y-6">
        {Object.entries(experiencesByParkGrouped).map(([park, experiences]) => (
          <div key={park} className="space-y-3">
            <h3 className="pb-2 text-lg font-semibold text-gray-800 border-b border-gray-200">
              {park}
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {experiences.map((experience) => {
                const isSelected = current.includes(experience.name);
                const isDisabled = false; // Add any disabled logic here if needed

                return (
                  <div
                    key={experience.name}
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-500'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => !isDisabled && handleExperienceToggle(experience.name)}
                  >
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => !isDisabled && handleExperienceToggle(experience.name)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-gray-900">
                            {experience.name}
                          </h4>
                          <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                            {experience.category}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-600">
                          {experience.location}
                        </p>
                        <p className="mt-1 text-sm text-gray-700">
                          {experience.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* No Experiences Message */}
      {Object.keys(experiencesByParkGrouped).length === 0 && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-center text-gray-600">
            No premium experiences available for your selected parks.
          </p>
        </div>
      )}
    </div>
  );
};

export default ExperienceSelector;
